import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { MAX_DELTA_REQUIRED_PRESSURE } from "../../constants";
import { doOneSet } from "./doOneSet";
import type { HypotheticalFlowDataRow, HypotheticalFlowDataSet } from "./d";
import { getHypotheticalFlowRates } from "./getHypotheticalFlowRates";
export * from "./d";

export const selectBestThresholdRow = (
  rows: HypotheticalFlowDataRow[],
  requestedTargetFlowSTBD?: number
): HypotheticalFlowDataRow | undefined => {
  const thresholdRows = rows.filter(row => row.status === "threshold-found");
  if (!thresholdRows.length) {
    return undefined;
  }

  const hasRequestedTarget = Number.isFinite(requestedTargetFlowSTBD);
  const targetFlow = hasRequestedTarget ? Number(requestedTargetFlowSTBD) : 0;

  return thresholdRows.reduce((bestRow, candidateRow) => {
    const bestFlowDistance = hasRequestedTarget
      ? Math.abs(bestRow.hypotheticalFlowRateSTBD - targetFlow)
      : Number.POSITIVE_INFINITY;
    const candidateFlowDistance = hasRequestedTarget
      ? Math.abs(candidateRow.hypotheticalFlowRateSTBD - targetFlow)
      : Number.POSITIVE_INFINITY;

    if (candidateFlowDistance < bestFlowDistance) {
      return candidateRow;
    }
    if (candidateFlowDistance > bestFlowDistance) {
      return bestRow;
    }

    const bestPressureDistance = Math.abs(bestRow.pressureDifference);
    const candidatePressureDistance = Math.abs(candidateRow.pressureDifference);
    if (candidatePressureDistance < bestPressureDistance) {
      return candidateRow;
    }
    if (candidatePressureDistance > bestPressureDistance) {
      return bestRow;
    }

    return candidateRow.hypotheticalFlowRateSTBD >
      bestRow.hypotheticalFlowRateSTBD
      ? candidateRow
      : bestRow;
  });
};

export const getPredictedDesiredFlowRateSTBD = (
  scenarioCalculations: ScenarioCalculations,
  _parameters: OperatingParameters,
  maxDeltaRequiredPressure = MAX_DELTA_REQUIRED_PRESSURE,
  maxDepth = 8 // Default from doOneRow.ts
): HypotheticalFlowDataSet => {
  const primaryPump = scenarioCalculations.primaryPump.value;
  const pumpHz = scenarioCalculations.pumpHz.value;
  const maxFlowRateAtIntakeBBLD =
    scenarioCalculations.maxFlowRateAtIntakeBBLD.value;
  const individualPumpCalculations =
    scenarioCalculations.pumpCalculations.individual.value;

  if (!primaryPump) {
    throw new Error("No primary pump provided");
  } else if (isNaN(maxFlowRateAtIntakeBBLD)) {
    throw new Error("Max flow rate at intake is NaN");
  }

  // ------------------------------------
  const doOneSetWithParams = (maxFlowRate: number, depth: number) => {
    const hypotheticalFlowRates = getHypotheticalFlowRates(maxFlowRate);

    const resultSet = doOneSet(
      hypotheticalFlowRates,
      scenarioCalculations,
      _parameters,
      depth,
      maxDeltaRequiredPressure,
      maxDepth
    );

    if (resultSet.targetRow.status !== "result-found") {
      const foundThresholdRow = selectBestThresholdRow(
        resultSet.rows,
        _parameters?.target_liquid_rate_std_barrel_day
      );

      if (foundThresholdRow) {
        resultSet.targetRow = foundThresholdRow;
        resultSet.status = "threshold-found";
      }
    }

    const isResultFound = resultSet.status === "result-found";
    const isThresholdFound = resultSet.status === "threshold-found";

    return {
      resultSet,
      isResultFound,
      isThresholdFound
    };
  };
  // ------------------------------------

  // We used to run other result sets at graphMax and 150k bbld
  // but they could give results greater than max rate at intake
  // which don't make much sense
  const resultSetMaxFlowAtIntake = doOneSetWithParams(
    maxFlowRateAtIntakeBBLD,
    1
  );

  let resultSet;
  // Look for a result found first, then find
  // the best threshold result if found
  if (resultSetMaxFlowAtIntake.isResultFound)
    resultSet = resultSetMaxFlowAtIntake.resultSet;
  else if (resultSetMaxFlowAtIntake.isThresholdFound)
    resultSet = resultSetMaxFlowAtIntake.resultSet;
  else resultSet = resultSet || resultSetMaxFlowAtIntake.resultSet;

  const result: HypotheticalFlowDataSet = {
    ...resultSet,
    status: resultSet.targetRow.status,
    allResultSets: {
      resultSetRowsMaxFlowAtIntake: resultSetMaxFlowAtIntake.resultSet.rows
    },
    importantStuff: {
      graphqMax: individualPumpCalculations[primaryPump.id].graphMax,
      maxFlowRateAtIntakeBBLD,
      maxDeltaRequiredPressure,
      maxDepth,
      pumpHz
    }
  };

  return result;
};
